/**
 * SCRUM-231 — messages shown inside a publish-form section depending on an
 * answer given in that section (e.g. יד שנייה → קטגוריה "כלי נשק").
 * Managed in the admin dashboard (מערכת התראות → הודעות בטופס הפרסום) and
 * served with each section by /dynamicForm/ as `answerMessages`.
 * The Flutter app implements the same matching (lib/utils/answer_messages.dart).
 */
export interface AnswerValue {
  value: string;
  label: string;
}

export interface SectionAnswerMessage {
  id: number;
  questionKey: string;
  /** Empty = any answer (for a checkbox: ticked). */
  values: AnswerValue[];
  text: string;
  level: 'info' | 'warning';
}

const norm = (v: unknown): string => String(v ?? '').trim().toLowerCase();

/** True when the user actually answered (not empty / unticked). */
export function hasAnswer(answer: unknown): boolean {
  if (answer === null || answer === undefined) return false;
  if (typeof answer === 'boolean') return answer;
  if (typeof answer === 'string') {
    const text = answer.trim().toLowerCase();
    return text !== '' && text !== 'false' && text !== 'none' && text !== 'null';
  }
  if (Array.isArray(answer)) return answer.some(hasAnswer);
  if (typeof answer === 'object') return answerCandidates(answer).length > 0;
  return true;
}

/** Every id / label inside an answer ("57", "כלי נשק", ...), normalized. */
export function answerCandidates(answer: unknown, depth = 0): string[] {
  if (answer === null || answer === undefined || depth > 3) return [];
  if (Array.isArray(answer)) return answer.flatMap((a) => answerCandidates(a, depth + 1));
  if (typeof answer === 'object') {
    const o = answer as Record<string, unknown>;
    return ['label', 'value', 'id', 'title', 'nameHe', 'subCategoryName']
      .flatMap((k) => answerCandidates(o[k], depth + 1));
  }
  const text = norm(answer);
  return text ? [text] : [];
}

export function answerMatches(rule: SectionAnswerMessage, answer: unknown): boolean {
  if (!hasAnswer(answer)) return false;
  if (!rule.values?.length) return true;
  const candidates = new Set(answerCandidates(answer));
  return rule.values.some((v) => candidates.has(norm(v.value)) || candidates.has(norm(v.label)));
}

/** The rules of a section whose question currently has a matching answer. */
export function matchingAnswerMessages(
  rules: SectionAnswerMessage[] | null | undefined,
  getAnswer: (key: string) => unknown
): SectionAnswerMessage[] {
  return (rules ?? []).filter((rule) => answerMatches(rule, getAnswer(rule.questionKey)));
}
